import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Download, ChevronDown, ChevronUp, Check, AlertTriangle, Info, Users, Search, Scale, RefreshCw, ArrowUpDown, TrendingUp, TrendingDown, LayoutGrid } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../contexts/ToastContext';
import { useAllocation } from '../../contexts/AllocationContext';
import { useData } from '../../contexts/DataContext';
import { gridsService } from '../../services/gridsService';
import { DEFAULT_BASE_MINUTES_PER_FORTNIGHT } from '../../types/schoolConfig';
import { resolveClassMinutes } from '../../utils/classMinutes';
import { useLearningAreas } from '../../hooks/useLearningAreas';
import { displayLearningArea } from '../../constants/learningAreas';
interface TeacherWorkload {
  id: string;
  name: string;
  code: string;
  department: string;
  fte: number;
  targetMinutes: number;
  allocatedMinutes: number;
  utilization: number;
  // Per-semester data
  semester1: {
    allocatedMinutes: number;
    targetMinutes: number;
    utilization: number;
    status: 'underloaded' | 'optimal' | 'overloaded';
  };
  semester2: {
    allocatedMinutes: number;
    targetMinutes: number;
    utilization: number;
    status: 'underloaded' | 'optimal' | 'overloaded';
  };
  classes: number;
  polAllowance: number;
  otherAllowances: number;
  status: 'underloaded' | 'optimal' | 'overloaded';
  classDetails: {
    subjectCode: string;
    subjectName: string;
    yearLevel: number;
    minutes: number;
    periods: number;
    complexity: 'low' | 'medium' | 'high';
    semester: 'semester1' | 'semester2';
  }[];
  allowanceDetails: {
    type: string;
    minutes: number;
  }[];
}

interface WorkloadEquityData {
  overallEquity: number;
  departmentEquity: {
    department: string;
    teacherCount: number;
    averageUtilization: number;
    standardDeviation: number;
    minUtilization: number;
    maxUtilization: number;
    equityScore: number;
  }[];
  utilizationDistribution: {
    range: string;
    count: number;
    percentage: number;
  }[];
  yearLevelDistribution: {
    yearLevel: number | string;
    teacherCount: number;
    averageUtilization: number;
    standardDeviation: number;
  }[];
  workloadGaps: {
    department: string;
    shortage: number;
    overallocation: number;
    netFTE: number;
  }[];
}

const WorkloadEquity: React.FC = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { getTeacherSemesterLoad } = useAllocation();
  const { staff: staffFromContext, loading: dataLoading } = useData();
  const { learningAreaNames } = useLearningAreas();
  const [activeGridCount, setActiveGridCount] = useState<number | null>(null);
  const [teacherWorkloads, setTeacherWorkloads] = useState<TeacherWorkload[]>([]);
  const [equityData, setEquityData] = useState<WorkloadEquityData | null>(null);
  const [selectedTeacher, setSelectedTeacher] = useState<string | null>(null);
  const [filterDepartment, setFilterDepartment] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedSection, setExpandedSection] = useState<string | null>('overview');
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Load real data from database once the staff fetch has settled.
  // We wait for DataContext to finish loading so an empty staff list is
  // treated as "no staff" rather than getting stuck on the loading spinner.
  useEffect(() => {
    if (dataLoading) return;
    loadRealData();
  }, [staffFromContext, dataLoading]); // Re-run when staff data settles

  const loadRealData = async () => {
    try {
      setIsLoading(true);
      setIsCalculating(true);

      // Fetch grids and use staff from DataContext
      // DataContext auto-generates teacher codes if initial is null
      const grids = await gridsService.getAllGrids();
      const staff = staffFromContext || [];

      const activeGrids = grids.filter(grid => grid.isActive !== false);
      setActiveGridCount(activeGrids.length);

      // Transform staff data into TeacherWorkload format
      const teacherWorkloads: TeacherWorkload[] = staff
        .map(staffMember => {
            // Use staff_code (staffCode property in the context)
            const staffCode = staffMember.staffCode || 'UNK';

            // Calculate allocated minutes and class details from grid assignments
            const classDetails: TeacherWorkload['classDetails'] = [];
            let allocatedMinutes = 0;
            let semester1AllocatedMinutes = 0;
            let semester2AllocatedMinutes = 0;

            // Iterate through all active grids
            activeGrids.forEach(grid => {
              // FIXED: Filter out composite sub-classes to avoid double-counting minutes
              // Iterate through all classes in the grid (excluding composite sub-classes)
              grid.classes
                .filter(classItem => !classItem.isCompositeSubClass)
                .forEach(classItem => {
                  // Check all line assignments for this class
                  Object.entries(classItem.lineAssignments || {}).forEach(([lineId, teacherCode]) => {
                    // Match against staffCode
                    if (teacherCode === staffMember.staffCode || teacherCode === staffMember.id) {
                    // Find the line to determine semester
                    const line = grid.lines.find(l => l.id === lineId);

                    if (line) {
                      // Calculate minutes for this class (default 440 minutes per fortnight per line)
                      const classMinutes = resolveClassMinutes(classItem, grid);
                      allocatedMinutes += classMinutes;

                      // Track per-semester allocated minutes
                      if (line.studyPeriod === 'semester1') {
                        semester1AllocatedMinutes += classMinutes;
                      } else if (line.studyPeriod === 'semester2') {
                        semester2AllocatedMinutes += classMinutes;
                      }

                      // Estimate periods (assuming 60 minutes per period as default)
                      const periods = Math.round(classMinutes / 60);

                      // Determine complexity based on year level
                      let complexity: 'low' | 'medium' | 'high' = 'medium';
                      if (classItem.yearLevel) {
                        if (classItem.yearLevel >= 11) complexity = 'high';
                        else if (classItem.yearLevel <= 8) complexity = 'low';
                      }

                      classDetails.push({
                        subjectCode: classItem.subjectCode || classItem.className,
                        subjectName: classItem.className,
                        yearLevel: classItem.yearLevel || 0,
                        minutes: classMinutes,
                        periods: periods,
                        complexity: complexity,
                        semester: line.studyPeriod as 'semester1' | 'semester2'
                      });
                    }
                  }
                });
              });
            });

            // Calculate total allowances from semester data
            let polAllowance = 0;
            let otherAllowances = 0;
            const allowanceDetails: { type: string; minutes: number; }[] = [];

            // Get allowances from both semesters and 'both'
            const allAllowances = [
              ...(staffMember.semester1?.allowances || []),
              ...(staffMember.semester2?.allowances || []),
              ...(staffMember.both?.allowances || [])
            ].filter(a => a && a.id); // Filter out null/undefined entries

            // Deduplicate allowances by ID
            const uniqueAllowances = Array.from(
              new Map(allAllowances.map(a => [a.id, a])).values()
            );

            uniqueAllowances.forEach(allowance => {
              // Safely get minutes value
              const minutes = allowance.minutes || 0;

              // Check if it's a POL allowance
              if (allowance.name && (allowance.name.toLowerCase().includes('pol') ||
                  allowance.name.toLowerCase().includes('position of leadership'))) {
                polAllowance += minutes;
              } else {
                otherAllowances += minutes;
              }

              allowanceDetails.push({
                type: allowance.name || 'Unknown',
                minutes: minutes
              });
            });

            // Calculate target minutes based on FTE (using configured base minutes per fortnight for full-time)
            const baseMinutes = staffMember.baseMinutesPerFortnight || DEFAULT_BASE_MINUTES_PER_FORTNIGHT;
            const targetMinutes = Math.round(baseMinutes * staffMember.fte);

            // Calculate per-semester allowances (including 'both' allowances)
            const semester1Allowances = [
              ...(staffMember.semester1?.allowances || []),
              ...(staffMember.both?.allowances || [])
            ].filter(a => a && a.minutes !== undefined);

            const semester2Allowances = [
              ...(staffMember.semester2?.allowances || []),
              ...(staffMember.both?.allowances || [])
            ].filter(a => a && a.minutes !== undefined);

            const semester1AllowanceMinutes = semester1Allowances.reduce((sum, a) => sum + (a.minutes || 0), 0);
            const semester2AllowanceMinutes = semester2Allowances.reduce((sum, a) => sum + (a.minutes || 0), 0);

            // Calculate per-semester target minutes
            const semester1TargetMinutes = Math.max(0, targetMinutes - semester1AllowanceMinutes);
            const semester2TargetMinutes = Math.max(0, targetMinutes - semester2AllowanceMinutes);

            // Calculate per-semester utilization
            const semester1Utilization = semester1TargetMinutes > 0
              ? Math.round((semester1AllocatedMinutes / semester1TargetMinutes) * 100)
              : 0;
            const semester2Utilization = semester2TargetMinutes > 0
              ? Math.round((semester2AllocatedMinutes / semester2TargetMinutes) * 100)
              : 0;

            // Determine per-semester status
            const getSemesterStatus = (util: number): 'underloaded' | 'optimal' | 'overloaded' => {
              if (util < 85) return 'underloaded';
              if (util <= 105) return 'optimal';
              return 'overloaded';
            };

            // Calculate overall utilization as the average of both semesters
            // This makes more sense than combining them, since each semester is independent
            const utilization = Math.round((semester1Utilization + semester2Utilization) / 2);

            // Determine overall status based on the worse of the two semesters
            // If either semester is overloaded, the overall status should reflect that
            const maxUtilization = Math.max(semester1Utilization, semester2Utilization);
            let status: 'underloaded' | 'optimal' | 'overloaded';
            if (maxUtilization < 85) {
              status = 'underloaded';
            } else if (maxUtilization <= 105) {
              status = 'optimal';
            } else {
              status = 'overloaded';
            }

            // For equity analysis, calculate total allocated vs total target (both semesters)
            const adjustedTargetMinutes = Math.max(0, targetMinutes - polAllowance - otherAllowances);

            return {
              id: staffMember.id,
              name: staffMember.name,
              code: staffCode,
              department: staffMember.department || 'Unknown',
              fte: staffMember.fte,
              targetMinutes: adjustedTargetMinutes,
              allocatedMinutes: allocatedMinutes,
              utilization: utilization,
              semester1: {
                allocatedMinutes: semester1AllocatedMinutes,
                targetMinutes: semester1TargetMinutes,
                utilization: semester1Utilization,
                status: getSemesterStatus(semester1Utilization)
              },
              semester2: {
                allocatedMinutes: semester2AllocatedMinutes,
                targetMinutes: semester2TargetMinutes,
                utilization: semester2Utilization,
                status: getSemesterStatus(semester2Utilization)
              },
              classes: classDetails.length,
              polAllowance: polAllowance,
              otherAllowances: otherAllowances,
              status: status,
              classDetails: classDetails,
              allowanceDetails: allowanceDetails
            };
          });

      setTeacherWorkloads(teacherWorkloads);

      // Calculate equity data
      if (teacherWorkloads.length > 0) {
        calculateEquity(teacherWorkloads);
      } else {
        // No staff to analyse — clear any prior result and the calculating flag
        setEquityData(null);
        setIsCalculating(false);
      }

      setIsLoading(false);
    } catch (error) {
      console.error('❌ WorkloadEquity: Error loading workload data:', error);
      console.error('❌ WorkloadEquity: Error stack:', error instanceof Error ? error.stack : 'No stack trace');
      addToast(`Failed to load workload data: ${error instanceof Error ? error.message : String(error)}`, 'error');
      setIsLoading(false);
      setIsCalculating(false);
    }
  };
  // Calculate equity data
  const calculateEquity = (workloads: TeacherWorkload[]) => {
    setIsCalculating(true);

    // Run the equity calculation synchronously (no artificial delay).
    {
      // Initialize data structure
      const data: WorkloadEquityData = {
        overallEquity: 0,
        departmentEquity: [],
        utilizationDistribution: [],
        yearLevelDistribution: [],
        workloadGaps: []
      };

      // Calculate department equity
      const departmentMap = new Map<
        string,
        {
          department: string;
          teacherCount: number;
          totalUtilization: number;
          utilizations: number[];
          minUtilization: number;
          maxUtilization: number;
        }
      >();

      // Process each teacher
      workloads.forEach(teacher => {
        // Get department data or initialize
        let dept = departmentMap.get(teacher.department);
        if (!dept) {
          dept = {
            department: teacher.department,
            teacherCount: 0,
            totalUtilization: 0,
            utilizations: [],
            minUtilization: Infinity,
            maxUtilization: -Infinity
          };
          departmentMap.set(teacher.department, dept);
        }

        dept.teacherCount++;
        dept.totalUtilization += teacher.utilization;
        dept.utilizations.push(teacher.utilization);
        dept.minUtilization = Math.min(dept.minUtilization, teacher.utilization);
        dept.maxUtilization = Math.max(dept.maxUtilization, teacher.utilization);
      });

      // Calculate equity scores for each department
      departmentMap.forEach((dept, deptName) => {
        const averageUtilization = dept.totalUtilization / dept.teacherCount;

        // Calculate standard deviation
        const sumSquaredDiff = dept.utilizations.reduce((sum, util) => {
          return sum + Math.pow(util - averageUtilization, 2);
        }, 0);
        const standardDeviation = Math.sqrt(sumSquaredDiff / dept.teacherCount);

        // Calculate equity score (higher is better)
        // Based on coefficient of variation (lower CV = higher equity)
        // Prevent division by zero - if averageUtilization is 0, set equity to 0
        const cv = averageUtilization > 0 ? standardDeviation / averageUtilization : 0;
        const equityScore = averageUtilization > 0 ? Math.round(100 * (1 - Math.min(cv, 0.5) / 0.5)) : 0;

        data.departmentEquity.push({
          department: deptName,
          teacherCount: dept.teacherCount,
          averageUtilization: Math.round(averageUtilization),
          standardDeviation: Math.round(standardDeviation * 10) / 10,
          minUtilization: dept.minUtilization,
          maxUtilization: dept.maxUtilization,
          equityScore
        });
      });

      // Calculate utilization distribution
      const utilizationRanges = [
        { range: '<70%', count: 0, percentage: 0 },
        { range: '70-85%', count: 0, percentage: 0 },
        { range: '85-95%', count: 0, percentage: 0 },
        { range: '95-105%', count: 0, percentage: 0 },
        { range: '105-115%', count: 0, percentage: 0 },
        { range: '>115%', count: 0, percentage: 0 }
      ];

      workloads.forEach(teacher => {
        if (teacher.utilization < 70) {
          utilizationRanges[0].count++;
        } else if (teacher.utilization < 85) {
          utilizationRanges[1].count++;
        } else if (teacher.utilization < 95) {
          utilizationRanges[2].count++;
        } else if (teacher.utilization < 105) {
          utilizationRanges[3].count++;
        } else if (teacher.utilization < 115) {
          utilizationRanges[4].count++;
        } else {
          utilizationRanges[5].count++;
        }
      });

      // Calculate percentages
      utilizationRanges.forEach(range => {
        range.percentage = Math.round((range.count / workloads.length) * 100);
      });

      data.utilizationDistribution = utilizationRanges;

      // Calculate year level distribution
      const yearLevelMap = new Map<
        number | string,
        {
          yearLevel: number | string;
          teacherCount: number;
          totalUtilization: number;
          utilizations: number[];
        }
      >();

      // Initialize year levels
      const yearLevels = [7, 8, 9, 10, 11, 12, 'mixed'];
      yearLevels.forEach(level => {
        yearLevelMap.set(level, {
          yearLevel: level,
          teacherCount: 0,
          totalUtilization: 0,
          utilizations: []
        });
      });

      // Process teachers by their primary year level
      workloads.forEach(teacher => {
        // Count classes by year level
        const yearLevelCounts = new Map<number, number>();
        teacher.classDetails.forEach(cls => {
          const count = yearLevelCounts.get(cls.yearLevel) || 0;
          yearLevelCounts.set(cls.yearLevel, count + 1);
        });

        // Find primary year level (the one with most classes)
        let primaryYearLevel: number | string = 'mixed';
        let maxClasses = 0;
        yearLevelCounts.forEach((count, yearLevel) => {
          if (count > maxClasses) {
            maxClasses = count;
            primaryYearLevel = yearLevel;
          }
        });

        // If tied, mark as mixed
        const maxYearLevels = Array.from(yearLevelCounts.entries()).filter(
          ([, count]) => count === maxClasses
        );
        if (maxYearLevels.length > 1) {
          primaryYearLevel = 'mixed';
        }

        // Update year level data
        const yearLevel = yearLevelMap.get(primaryYearLevel);
        if (yearLevel) {
          yearLevel.teacherCount++;
          yearLevel.totalUtilization += teacher.utilization;
          yearLevel.utilizations.push(teacher.utilization);
        }
      });

      // Calculate statistics for each year level
      yearLevelMap.forEach((level, yearLevel) => {
        if (level.teacherCount > 0) {
          const averageUtilization = level.totalUtilization / level.teacherCount;

          // Calculate standard deviation
          const sumSquaredDiff = level.utilizations.reduce((sum, util) => {
            return sum + Math.pow(util - averageUtilization, 2);
          }, 0);
          const standardDeviation = Math.sqrt(sumSquaredDiff / level.teacherCount);

          data.yearLevelDistribution.push({
            yearLevel,
            teacherCount: level.teacherCount,
            averageUtilization: Math.round(averageUtilization),
            standardDeviation: Math.round(standardDeviation * 10) / 10
          });
        }
      });

      // Sort year level distribution by year level
      data.yearLevelDistribution.sort((a, b) => {
        if (a.yearLevel === 'mixed') return 1;
        if (b.yearLevel === 'mixed') return -1;
        return Number(a.yearLevel) - Number(b.yearLevel);
      });

      // Calculate workload gaps
      const departmentFTEMap = new Map<
        string,
        {
          department: string;
          requiredFTE: number;
          allocatedFTE: number;
        }
      >();

      // Initialize departments
      Array.from(departmentMap.keys()).forEach(dept => {
        departmentFTEMap.set(dept, {
          department: dept,
          requiredFTE: 0,
          allocatedFTE: 0
        });
      });

          // Calculate required vs allocated FTE
      workloads.forEach(teacher => {
        const dept = departmentFTEMap.get(teacher.department);
        if (dept) {
          // Required FTE based on allocated minutes (2220 minutes = 1.0 FTE)
          const requiredFTE = teacher.allocatedMinutes / 2220;
          dept.requiredFTE += requiredFTE;
          dept.allocatedFTE += teacher.fte;
        }
      });

      // Calculate gaps
      departmentFTEMap.forEach((dept, deptName) => {
        const netFTE = dept.allocatedFTE - dept.requiredFTE;
        data.workloadGaps.push({
          department: deptName,
          shortage: netFTE < 0 ? Math.abs(Math.round(netFTE * 100) / 100) : 0,
          overallocation: netFTE > 0 ? Math.round(netFTE * 100) / 100 : 0,
          netFTE: Math.round(netFTE * 100) / 100
        });
      });

      // Calculate overall equity
      // Based on standard deviation of all utilizations
      const allUtilizations = workloads.map(t => t.utilization);
      const avgUtilization =
        allUtilizations.reduce((sum, util) => sum + util, 0) / allUtilizations.length;
      const sumSquaredDiff = allUtilizations.reduce((sum, util) => {
        return sum + Math.pow(util - avgUtilization, 2);
      }, 0);
      const overallStdDev = Math.sqrt(sumSquaredDiff / allUtilizations.length);

      // Calculate overall equity score
      // Prevent division by zero - if avgUtilization is 0, set equity to 0
      const overallCV = avgUtilization > 0 ? overallStdDev / avgUtilization : 0;
      data.overallEquity = avgUtilization > 0 ? Math.round(100 * (1 - Math.min(overallCV, 0.5) / 0.5)) : 0;

      // Update state
      setEquityData(data);
      setIsCalculating(false);
    }
  };

  // Get filtered teacher workloads
  const getFilteredTeacherWorkloads = () => {
    return teacherWorkloads.filter(teacher => {
      // Department filter
      if (filterDepartment !== 'all' && displayLearningArea(teacher.department) !== filterDepartment) {
        return false;
      }
      // Status filter
      if (filterStatus !== 'all' && teacher.status !== filterStatus) {
        return false;
      }
      // Search query
      if (searchQuery && !teacher.name.toLowerCase().includes(searchQuery.toLowerCase()) && !teacher.code.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      return true;
    }).sort((a, b) => {
      // Sort by selected field
      if (sortField === 'name') {
        return sortDirection === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      } else if (sortField === 'department') {
        return sortDirection === 'asc' ? a.department.localeCompare(b.department) : b.department.localeCompare(a.department);
      } else if (sortField === 'fte') {
        return sortDirection === 'asc' ? a.fte - b.fte : b.fte - a.fte;
      } else if (sortField === 'utilization') {
        return sortDirection === 'asc' ? a.utilization - b.utilization : b.utilization - a.utilization;
      } else if (sortField === 'semester1Utilization') {
        return sortDirection === 'asc' ? a.semester1.utilization - b.semester1.utilization : b.semester1.utilization - a.semester1.utilization;
      } else if (sortField === 'semester2Utilization') {
        return sortDirection === 'asc' ? a.semester2.utilization - b.semester2.utilization : b.semester2.utilization - a.semester2.utilization;
      } else if (sortField === 'classes') {
        return sortDirection === 'asc' ? a.classes - b.classes : b.classes - a.classes;
      }
      return 0;
    });
  };

  // Get unique departments
  const getUniqueDepartments = () => {
    const departments = new Set<string>();
    teacherWorkloads.forEach(teacher => {
      departments.add(teacher.department);
    });
    return Array.from(departments);
  };

  // Export the on-screen teacher workload table to CSV (client-side, no server call)
  const exportToCSV = () => {
    const rows = getFilteredTeacherWorkloads();
    if (rows.length === 0) {
      addToast('No teacher workloads to export with the current filters.', 'info');
      return;
    }

    const headers = [
      'Teacher', 'Code', 'Learning Area', 'FTE',
      'Target Minutes', 'Allocated Minutes',
      'S1 Allocated', 'S1 Target', 'S1 Utilisation %',
      'S2 Allocated', 'S2 Target', 'S2 Utilisation %',
      'Overall Utilisation %', 'Status', 'Classes',
      'POL Allowance (min)', 'Other Allowance (min)'
    ];

    const escape = (value: string | number) => `"${String(value).replace(/"/g, '""')}"`;

    const dataRows = rows.map(t => [
      t.name, t.code, t.department, t.fte.toFixed(2),
      t.targetMinutes, t.allocatedMinutes,
      t.semester1.allocatedMinutes, t.semester1.targetMinutes, t.semester1.utilization,
      t.semester2.allocatedMinutes, t.semester2.targetMinutes, t.semester2.utilization,
      t.utilization, t.status, t.classes,
      t.polAllowance, t.otherAllowances
    ]);

    const csvContent = [
      headers.map(escape).join(','),
      ...dataRows.map(row => row.map(escape).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `workload-equity-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    // Clean up the blob URL to prevent a memory leak
    setTimeout(() => URL.revokeObjectURL(url), 100);

    addToast('Workload equity data exported as CSV.', 'success');
  };

  // Toggle section expansion
  const toggleSection = (section: string) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };

  // Toggle sort direction
  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get utilization color
  const getUtilizationColor = (utilization: number) => {
    if (utilization < 70) {
      return 'text-navy-600';
    } else if (utilization < 85) {
      return 'text-navy-500';
    } else if (utilization < 95) {
      return 'text-ok-fg';
    } else if (utilization < 105) {
      return 'text-ok-fg';
    } else if (utilization < 115) {
      return 'text-warn-fg';
    } else {
      return 'text-danger-fg';
    }
  };

  // Get equity score color
  const getEquityScoreColor = (score: number) => {
    if (score >= 80) {
      return 'text-ok-fg';
    } else if (score >= 60) {
      return 'text-warn-fg';
    } else {
      return 'text-danger-fg';
    }
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'underloaded':
        return (
          <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-navy-100 text-navy-800">
            Underloaded
          </span>
        );
      case 'optimal':
        return (
          <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-ok-bg text-ok-fg">
            Optimal
          </span>
        );
      case 'overloaded':
        return (
          <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-danger-bg text-danger-fg">
            Overloaded
          </span>
        );
      default:
        return null;
    }
  };

  // Render teacher details
  const renderTeacherDetails = (teacherId: string) => {
    const teacher = teacherWorkloads.find(t => t.id === teacherId);
    if (!teacher) return null;

    return (
      <Card className="mt-4">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-body-sm font-medium text-ink-700">
            Workload Details for {teacher.name}
          </h4>
          <div className="flex items-center space-x-2">
            <div className={`text-lg font-medium font-mono tabular-nums ${getUtilizationColor(teacher.utilization)}`}>
              {teacher.utilization}%
            </div>
            {getStatusBadge(teacher.status)}
          </div>
        </div>

        <div className="space-y-4">
          {/* Per-Semester Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-navy-50 p-4 rounded-md border border-navy-200">
              <div className="flex justify-between items-center mb-2">
                <div className="text-body-sm font-medium text-navy-900">Semester 1</div>
                <div className={`text-lg font-medium font-mono tabular-nums ${getUtilizationColor(teacher.semester1.utilization)}`}>
                  {teacher.semester1.utilization}%
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-body-sm">
                  <span className="text-navy-700">Allocated:</span>
                  <span className="font-medium text-navy-900 font-mono tabular-nums">{teacher.semester1.allocatedMinutes} min</span>
                </div>
                <div className="flex justify-between text-body-sm">
                  <span className="text-navy-700">Target:</span>
                  <span className="font-medium text-navy-900 font-mono tabular-nums">{teacher.semester1.targetMinutes} min</span>
                </div>
                <div className="flex justify-between text-body-sm">
                  <span className="text-navy-700">Status:</span>
                  <span>{getStatusBadge(teacher.semester1.status)}</span>
                </div>
              </div>
            </div>
            <div className="bg-navy-50 p-4 rounded-md border border-navy-200">
              <div className="flex justify-between items-center mb-2">
                <div className="text-body-sm font-medium text-navy-900">Semester 2</div>
                <div className={`text-lg font-medium font-mono tabular-nums ${getUtilizationColor(teacher.semester2.utilization)}`}>
                  {teacher.semester2.utilization}%
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-body-sm">
                  <span className="text-navy-700">Allocated:</span>
                  <span className="font-medium text-navy-900 font-mono tabular-nums">{teacher.semester2.allocatedMinutes} min</span>
                </div>
                <div className="flex justify-between text-body-sm">
                  <span className="text-navy-700">Target:</span>
                  <span className="font-medium text-navy-900 font-mono tabular-nums">{teacher.semester2.targetMinutes} min</span>
                </div>
                <div className="flex justify-between text-body-sm">
                  <span className="text-navy-700">Status:</span>
                  <span>{getStatusBadge(teacher.semester2.status)}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-md border">
              <div className="text-meta text-ink-500">FTE</div>
              <div className="text-lg font-medium text-ink-900 font-mono tabular-nums">
                {teacher.fte.toFixed(1)}
              </div>
              <div className="text-meta text-ink-500 mt-1">
                Target: {teacher.targetMinutes} minutes
              </div>
            </div>

            <div className="bg-white p-4 rounded-md border">
              <div className="text-meta text-ink-500">Total Allocated</div>
              <div className="text-lg font-medium text-ink-900 font-mono tabular-nums">
                {teacher.allocatedMinutes} minutes
              </div>
              <div className="text-meta text-ink-500 mt-1">{teacher.classes} classes</div>
            </div>

            <div className="bg-white p-4 rounded-md border">
              <div className="text-meta text-ink-500">Allowances</div>
              <div className="text-lg font-medium text-ink-900 font-mono tabular-nums">
                {teacher.polAllowance + teacher.otherAllowances} minutes
              </div>
              <div className="text-meta text-ink-500 mt-1">
                {teacher.polAllowance > 0 ? `POL: ${teacher.polAllowance} min` : ''}
                {teacher.polAllowance > 0 && teacher.otherAllowances > 0 ? ' / ' : ''}
                {teacher.otherAllowances > 0 ? `Other: ${teacher.otherAllowances} min` : ''}
              </div>
            </div>
          </div>

          <div>
            <h5 className="text-body-sm font-medium text-ink-700 mb-2">Classes</h5>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-ink-200">
                <thead className="bg-ink-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                      Subject
                    </th>
                    <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                      Year Level
                    </th>
                    <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                      Semester
                    </th>
                    <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                      Periods
                    </th>
                    <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                      Minutes
                    </th>
                    <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                      Complexity
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-ink-200">
                  {teacher.classDetails.map((cls, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-body-sm font-medium text-ink-900">
                          {cls.subjectName}
                        </div>
                        <div className="text-meta text-ink-500 font-mono tabular-nums">{cls.subjectCode}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                        {cls.yearLevel}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full ${cls.semester === 'semester1' ? 'bg-navy-100 text-navy-800' : 'bg-navy-100 text-navy-800'}`}>
                          {cls.semester === 'semester1' ? 'S1' : 'S2'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                        {cls.periods}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                        {cls.minutes}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full ${
                            cls.complexity === 'high'
                              ? 'bg-warn-bg text-warn-fg'
                              : cls.complexity === 'medium'
                              ? 'bg-warn-bg text-warn-fg'
                              : 'bg-ok-bg text-ok-fg'
                          }`}
                        >
                          {cls.complexity.charAt(0).toUpperCase() + cls.complexity.slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {teacher.allowanceDetails.map((allowance, index) => (
                    <tr key={`allowance-${index}`} className="bg-ink-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-body-sm font-medium text-ink-900">
                          {allowance.type}
                        </div>
                        <div className="text-meta text-ink-500">Allowance</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-body-sm text-ink-500">-</td>
                      <td className="px-4 py-3 whitespace-nowrap text-body-sm text-ink-500">
                        -
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                        {Math.round(allowance.minutes / 60)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                        {allowance.minutes}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-body-sm text-ink-500">-</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-navy-50 p-4 rounded-md border border-navy-200">
            <div className="flex">
              <Info className="h-5 w-5 text-navy-500 mr-3 mt-0.5" />
              <div>
                <h4 className="text-body-sm font-medium text-navy-800 mb-1">Workload Analysis</h4>
                <p className="text-body-sm text-navy-700">
                  {teacher.name}'s workload is currently at{' '}
                  <span className="font-medium">{teacher.utilization}%</span> utilization.
                  {teacher.utilization < 85
                    ? ' This teacher is underloaded compared to their FTE allocation.'
                    : teacher.utilization <= 105
                    ? ' This teacher has an optimal workload balance.'
                    : ' This teacher is overloaded compared to their FTE allocation.'}
                  {teacher.polAllowance > 0
                    ? ` They have ${teacher.polAllowance} minutes of Position of Leadership allowance.`
                    : ''}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="Workload Equity Analysis"
          description="Who is over- or under-loaded? Compares each teacher's allocated teaching minutes against their FTE target across both semesters."
        />
        <Card className="p-8">
          <div className="flex justify-center py-8">
            <Spinner size="lg" label="Loading teacher workload data…" />
          </div>
        </Card>
      </div>
    );
  }

  // Key equity insight: who is most over- and under-loaded (by overall utilisation)
  const rankedByUtil = [...teacherWorkloads].sort((a, b) => b.utilization - a.utilization);
  const mostOverloaded = rankedByUtil[0];
  const mostUnderloaded = rankedByUtil[rankedByUtil.length - 1];

  return (
    <div>
      <PageHeader
        title="Workload Equity Analysis"
        description="Who is over- or under-loaded? Compares each teacher's allocated teaching minutes against their FTE target across both semesters."
        actions={
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              leftIcon={<RefreshCw className="h-4 w-4" />}
              onClick={() => loadRealData()}
              loading={isCalculating}
            >
              Recalculate
            </Button>
            <Button
              variant="primary"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={exportToCSV}
              disabled={teacherWorkloads.length === 0}
            >
              Export CSV
            </Button>
          </div>
        }
      />

      {isCalculating ? (
        <Card className="p-8">
          <div className="flex justify-center py-8">
            <Spinner size="lg" label="Calculating workload equity…" />
          </div>
        </Card>
      ) : staffFromContext.length === 0 ? (
        <Card className="p-8 text-center">
          <Users className="h-12 w-12 text-ink-400 mx-auto mb-4" />
          <h3 className="text-card-title text-ink-900 mb-2">No Staff Found</h3>
          <p className="text-ink-500 max-w-md mx-auto">
            There are no staff records to analyse yet. Add staff in the Staff Directory,
            then return here to review workload equity.
          </p>
        </Card>
      ) : activeGridCount === 0 ? (
        <Card className="p-8 text-center">
          <LayoutGrid className="h-12 w-12 text-ink-400 mx-auto mb-4" />
          <h3 className="text-card-title text-ink-900 mb-2">No Active Grid Found</h3>
          <p className="text-ink-500 mb-4 max-w-md mx-auto">
            Workload is calculated from teaching allocations in the timetable grid. Once a
            grid is imported and set as active, each teacher's load and utilisation will
            appear here.
          </p>
          <Button
            variant="primary"
            leftIcon={<LayoutGrid className="h-4 w-4" />}
            onClick={() => navigate('/grid/multi-editor')}
          >
            Open Timetable Editor
          </Button>
        </Card>
      ) : equityData ? (
        <div className="space-y-6">
          {/* Overall Equity Card */}
          <Card>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-card-title text-ink-900">Overall Workload Equity</h3>
              <div className="flex items-center space-x-3">
                <div
                  className={`text-2xl font-bold font-mono tabular-nums ${getEquityScoreColor(
                    equityData.overallEquity
                  )}`}
                >
                  {equityData.overallEquity}%
                </div>
                <span
                  className={`px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full ${
                    equityData.overallEquity >= 80
                      ? 'bg-ok-bg text-ok-fg'
                      : equityData.overallEquity >= 60
                      ? 'bg-warn-bg text-warn-fg'
                      : 'bg-danger-bg text-danger-fg'
                  }`}
                >
                  {equityData.overallEquity >= 80
                    ? 'High Equity'
                    : equityData.overallEquity >= 60
                    ? 'Moderate Equity'
                    : 'Low Equity'}
                </span>
              </div>
            </div>

            {/* Key equity insight — who is most over- and under-loaded */}
            {mostOverloaded && mostUnderloaded && mostOverloaded.id !== mostUnderloaded.id && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="flex items-start p-4 rounded-md border bg-danger-bg">
                  <TrendingUp className="h-5 w-5 text-danger-fg mr-3 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-meta font-semibold text-danger-fg uppercase tracking-wide">
                      Most overloaded
                    </div>
                    <div className="text-body-sm font-medium text-ink-900">{mostOverloaded.name}</div>
                    <div className="text-meta text-ink-600">
                      {displayLearningArea(mostOverloaded.department)} ·{' '}
                      <span className="font-mono tabular-nums">{mostOverloaded.utilization}%</span>{' '}
                      utilisation
                    </div>
                  </div>
                </div>
                <div className="flex items-start p-4 rounded-md border border-navy-200 bg-navy-50">
                  <TrendingDown className="h-5 w-5 text-navy-600 mr-3 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-meta font-semibold text-navy-700 uppercase tracking-wide">
                      Most underloaded
                    </div>
                    <div className="text-body-sm font-medium text-ink-900">{mostUnderloaded.name}</div>
                    <div className="text-meta text-ink-600">
                      {displayLearningArea(mostUnderloaded.department)} ·{' '}
                      <span className="font-mono tabular-nums">{mostUnderloaded.utilization}%</span>{' '}
                      utilisation
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-md border">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-body-sm text-ink-500">Teachers</div>
                    <div className="text-2xl font-bold text-ink-900 font-mono tabular-nums">
                      {teacherWorkloads.length}
                    </div>
                  </div>
                  <div className="h-10 w-10 bg-navy-100 rounded-full flex items-center justify-center">
                    <Users className="h-5 w-5 text-navy-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-md border">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-body-sm text-ink-500">Average Utilization</div>
                    <div className="text-2xl font-bold text-ink-900 font-mono tabular-nums">
                      {teacherWorkloads.length > 0
                        ? Math.round(
                            teacherWorkloads.reduce((sum, t) => sum + t.utilization, 0) /
                              teacherWorkloads.length
                          )
                        : 0}
                      %
                    </div>
                  </div>
                  <div className="h-10 w-10 bg-ok-bg rounded-full flex items-center justify-center">
                    <BarChart className="h-5 w-5 text-ok-fg" />
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-md border">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-body-sm text-ink-500">Optimal Range</div>
                    <div className="text-2xl font-bold text-ok-fg font-mono tabular-nums">
                      {equityData.utilizationDistribution.find(d => d.range === '95-105%')
                        ?.percentage || 0}
                      %
                    </div>
                  </div>
                  <div className="h-10 w-10 bg-ok-bg rounded-full flex items-center justify-center">
                    <Check className="h-5 w-5 text-ok-fg" />
                  </div>
                </div>
                <div className="mt-1 text-meta text-ink-500">
                  Teachers with 95-105% utilization
                </div>
              </div>

              <div className="bg-white p-4 rounded-md border">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-body-sm text-ink-500">Workload Issues</div>
                    <div className="text-2xl font-bold text-warn-fg font-mono tabular-nums">
                      {(equityData.utilizationDistribution.find(d => d.range === '<70%')
                        ?.count || 0) +
                        (equityData.utilizationDistribution.find(d => d.range === '>115%')
                          ?.count || 0)}
                    </div>
                  </div>
                  <div className="h-10 w-10 bg-warn-bg rounded-full flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-warn-fg" />
                  </div>
                </div>
                <div className="mt-1 text-meta text-ink-500">
                  <span className="text-navy-600">
                    {equityData.utilizationDistribution.find(d => d.range === '<70%')
                      ?.count || 0}{' '}
                    severely underloaded
                  </span>{' '}
                  •
                  <span className="text-danger-fg ml-1">
                    {equityData.utilizationDistribution.find(d => d.range === '>115%')
                      ?.count || 0}{' '}
                    severely overloaded
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <h4 className="text-body-sm font-medium text-ink-700 mb-1">
                Utilization Distribution
              </h4>
              <p className="text-meta text-ink-500 mb-3">
                Share of teachers in each utilisation band (allocated teaching minutes ÷ FTE target).
              </p>
              <div className="bg-white p-4 rounded-md border">
                <div className="relative pt-1">
                  <div className="flex h-4 mb-4 overflow-hidden bg-ink-200 rounded">
                    {equityData.utilizationDistribution.map((range, index) => (
                      <div
                        key={index}
                        style={{ width: `${range.percentage}%` }}
                        className={`${
                          range.range === '<70%'
                            ? 'bg-navy-300'
                            : range.range === '70-85%'
                            ? 'bg-navy-500'
                            : range.range === '85-95%'
                            ? 'bg-ok-solid'
                            : range.range === '95-105%'
                            ? 'bg-ok-solid'
                            : range.range === '105-115%'
                            ? 'bg-warn-solid'
                            : 'bg-danger-solid'
                        }`}
                      ></div>
                    ))}
                  </div>
                  <div className="grid grid-cols-6 gap-1 text-center">
                    {equityData.utilizationDistribution.map((range, index) => (
                      <div key={index} className="text-meta">
                        <div
                          className={`font-medium font-mono tabular-nums ${
                            range.range === '<70%'
                              ? 'text-navy-600'
                              : range.range === '70-85%'
                              ? 'text-navy-700'
                              : range.range === '85-95%'
                              ? 'text-ok-fg'
                              : range.range === '95-105%'
                              ? 'text-ok-fg'
                              : range.range === '105-115%'
                              ? 'text-warn-fg'
                              : 'text-danger-fg'
                          }`}
                        >
                          {range.percentage}%
                        </div>
                        <div className="text-ink-500 font-mono tabular-nums">{range.range}</div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Band legend — spells out what each colour means (never colour alone) */}
                <div className="mt-4 pt-3 border-t flex flex-wrap gap-x-4 gap-y-1.5 text-meta text-ink-600">
                  <span className="inline-flex items-center">
                    <span className="w-2.5 h-2.5 rounded-sm bg-navy-500 mr-1.5"></span>
                    Under 85% — underloaded
                  </span>
                  <span className="inline-flex items-center">
                    <span className="w-2.5 h-2.5 rounded-sm bg-ok-solid mr-1.5"></span>
                    85–105% — optimal
                  </span>
                  <span className="inline-flex items-center">
                    <span className="w-2.5 h-2.5 rounded-sm bg-warn-solid mr-1.5"></span>
                    105–115% — overloaded
                  </span>
                  <span className="inline-flex items-center">
                    <span className="w-2.5 h-2.5 rounded-sm bg-danger-solid mr-1.5"></span>
                    Over 115% — severely overloaded
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-navy-50 p-4 rounded-md border border-navy-200">
              <div className="flex">
                <Scale className="h-5 w-5 text-navy-500 mr-3 mt-0.5" />
                <div>
                  <h4 className="text-body-sm font-medium text-navy-800 mb-1">Equity Summary</h4>
                  <p className="text-body-sm text-navy-700">
                    The overall workload equity score is {equityData.overallEquity}%,
                    indicating
                    {equityData.overallEquity >= 80
                      ? ' a high level of equity'
                      : equityData.overallEquity >= 60
                      ? ' a moderate level of equity'
                      : ' a low level of equity'}{' '}
                    in workload distribution.
                    {equityData.overallEquity >= 80
                      ? ' Workloads are well-balanced across teachers and learning areas.'
                      : equityData.overallEquity >= 60
                      ? ' There is some variation in workload distribution that could be improved.'
                      : ' There are significant imbalances in workload distribution that should be addressed.'}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Department Equity */}
          <Card>
            <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleSection('department')}>
              <h3 className="text-card-title text-ink-900">
                Learning Area Equity
              </h3>
              {expandedSection === 'department' ? <ChevronUp className="h-5 w-5 text-ink-500" /> : <ChevronDown className="h-5 w-5 text-ink-500" />}
            </div>
            {expandedSection === 'department' && <div className="mt-6 space-y-6">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-ink-200">
                    <thead className="bg-ink-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Learning Area
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Teachers
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Avg. Utilization
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Std. Deviation
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Range
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Equity Score
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-ink-200">
                      {equityData.departmentEquity.map((dept, index) => <tr key={index} className="hover:bg-ink-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-body-sm font-medium text-ink-900">
                              {displayLearningArea(dept.department)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                            {dept.teacherCount}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-body-sm font-medium font-mono tabular-nums ${getUtilizationColor(dept.averageUtilization)}`}>
                              {dept.averageUtilization}%
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                            {dept.standardDeviation}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                            {dept.minUtilization}% - {dept.maxUtilization}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className={`text-body-sm font-medium font-mono tabular-nums ${getEquityScoreColor(dept.equityScore)} mr-2`}>
                                {dept.equityScore}%
                              </div>
                              <span className={`px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full ${dept.equityScore >= 80 ? 'bg-ok-bg text-ok-fg' : dept.equityScore >= 60 ? 'bg-warn-bg text-warn-fg' : 'bg-danger-bg text-danger-fg'}`}>
                                {dept.equityScore >= 80 ? 'High' : dept.equityScore >= 60 ? 'Medium' : 'Low'}
                              </span>
                            </div>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-4 rounded-md border">
                    <h4 className="text-body-sm font-medium text-ink-700 mb-3">
                      Learning Area Utilization
                    </h4>
                    <div className="space-y-4">
                      {equityData.departmentEquity.map((dept, index) => <div key={index}>
                          <div className="flex justify-between items-center mb-1">
                            <div className="text-body-sm font-medium text-ink-700">
                              {displayLearningArea(dept.department)}
                            </div>
                            <div className={`text-body-sm font-medium font-mono tabular-nums ${getUtilizationColor(dept.averageUtilization)}`}>
                              {dept.averageUtilization}%
                            </div>
                          </div>
                          <div className="relative h-4 bg-ink-200 rounded overflow-hidden">
                            <div className={`absolute h-4 rounded-l ${dept.averageUtilization < 85 ? 'bg-navy-500' : dept.averageUtilization < 95 ? 'bg-ok-solid' : dept.averageUtilization <= 105 ? 'bg-ok-solid' : dept.averageUtilization <= 115 ? 'bg-warn-solid' : 'bg-danger-solid'}`} style={{
                      width: `${Math.min(dept.averageUtilization, 100)}%`,
                      left: '0%'
                    }}></div>
                            {/* Optimal range indicators at 95% and 105% - only show if within bounds */}
                            <div className="absolute h-4 border-l-2 border-ink-900 opacity-50" style={{
                      left: '95%'
                    }}></div>
                          </div>
                          <div className="flex justify-between text-meta text-ink-500 mt-1">
                            <span>
                              Range: {dept.minUtilization}% -{' '}
                              {dept.maxUtilization}%
                            </span>
                            <span>Std Dev: {dept.standardDeviation}</span>
                          </div>
                        </div>)}
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-md border">
                    <h4 className="text-body-sm font-medium text-ink-700 mb-3">
                      Learning Area Equity Scores
                    </h4>
                    <div className="space-y-4">
                      {equityData.departmentEquity.map((dept, index) => <div key={index}>
                          <div className="flex justify-between items-center mb-1">
                            <div className="text-body-sm font-medium text-ink-700">
                              {displayLearningArea(dept.department)}
                            </div>
                            <div className={`text-body-sm font-medium font-mono tabular-nums ${getEquityScoreColor(dept.equityScore)}`}>
                              {dept.equityScore}%
                            </div>
                          </div>
                          <div className="w-full bg-ink-200 rounded-full h-2.5">
                            <div className={`h-2.5 rounded-full ${dept.equityScore >= 80 ? 'bg-ok-solid' : dept.equityScore >= 60 ? 'bg-warn-solid' : 'bg-danger-solid'}`} style={{
                      width: `${dept.equityScore}%`
                    }}></div>
                          </div>
                          <div className="flex justify-between text-meta text-ink-500 mt-1">
                            <span>{dept.teacherCount} teachers</span>
                            <span>
                              {dept.equityScore >= 80 ? 'High equity' : dept.equityScore >= 60 ? 'Medium equity' : 'Low equity'}
                            </span>
                          </div>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>}
          </Card>

          {/* Workload Gaps */}
          <Card>
            <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleSection('gaps')}>
              <h3 className="text-card-title text-ink-900">
                Workload Gaps
              </h3>
              {expandedSection === 'gaps' ? <ChevronUp className="h-5 w-5 text-ink-500" /> : <ChevronDown className="h-5 w-5 text-ink-500" />}
            </div>
            {expandedSection === 'gaps' && <div className="mt-6 space-y-6">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-ink-200">
                    <thead className="bg-ink-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Learning Area
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Shortage (FTE)
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Overallocation (FTE)
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Net FTE
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-ink-200">
                      {equityData.workloadGaps.map((gap, index) => <tr key={index} className="hover:bg-ink-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-body-sm font-medium text-ink-900">
                              {displayLearningArea(gap.department)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-body-sm font-medium text-danger-fg font-mono tabular-nums">
                              {gap.shortage > 0 ? gap.shortage.toFixed(2) : '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-body-sm font-medium text-ok-fg font-mono tabular-nums">
                              {gap.overallocation > 0 ? gap.overallocation.toFixed(2) : '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-body-sm font-medium ${gap.netFTE >= 0 ? 'text-ok-fg' : 'text-danger-fg'}`}>
                              {gap.netFTE.toFixed(2)} FTE
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full ${gap.netFTE > 0.1 ? 'bg-ok-bg text-ok-fg' : gap.netFTE >= -0.1 ? 'bg-warn-bg text-warn-fg' : 'bg-danger-bg text-danger-fg'}`}>
                              {gap.netFTE > 0.1 ? 'Surplus' : gap.netFTE >= -0.1 ? 'Balanced' : 'Deficit'}
                            </span>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
                <div className="bg-ink-50 p-4 rounded-md border">
                  <h4 className="text-body-sm font-medium text-ink-700 mb-3">
                    FTE Allocation by Learning Area
                  </h4>
                  <div className="space-y-4">
                    {equityData.workloadGaps.map((gap, index) => <div key={index}>
                        <div className="flex justify-between items-center mb-1">
                          <div className="text-body-sm font-medium text-ink-700">
                            {displayLearningArea(gap.department)}
                          </div>
                          <div className={`text-body-sm font-medium font-mono tabular-nums ${gap.netFTE >= 0 ? 'text-ok-fg' : 'text-danger-fg'}`}>
                            {gap.netFTE.toFixed(2)} FTE
                          </div>
                        </div>
                        <div className="relative h-4 bg-ink-200 rounded">
                          {gap.netFTE >= 0 ? <div className="absolute h-4 bg-ok-solid rounded-l" style={{
                    width: `${Math.min(50 + gap.netFTE * 50, 100)}%`,
                    left: '0%'
                  }}></div> : <div className="absolute h-4 bg-danger-solid rounded-r" style={{
                    width: `${Math.min(50 + Math.abs(gap.netFTE) * 50, 100)}%`,
                    right: '0%'
                  }}></div>}
                          {/* Center line */}
                          <div className="absolute h-4 border-l-2 border-ink-900" style={{
                    left: '50%'
                  }}></div>
                        </div>
                        <div className="flex justify-between text-meta text-ink-500 mt-1">
                          <span>Deficit</span>
                          <span>Balanced</span>
                          <span>Surplus</span>
                        </div>
                      </div>)}
                  </div>
                </div>
                <div className="bg-navy-50 p-4 rounded-md border border-navy-200">
                  <div className="flex">
                    <Info className="h-5 w-5 text-navy-500 mr-3 mt-0.5" />
                    <div>
                      <h4 className="text-body-sm font-medium text-navy-800 mb-1">
                        Workload Gap Analysis
                      </h4>
                      <p className="text-body-sm text-navy-700">
                        This analysis compares allocated FTE with required FTE
                        based on actual workload. Learning areas with a negative
                        Net FTE are understaffed, while those with a positive
                        Net FTE have excess capacity.
                        {equityData.workloadGaps.some(g => g.netFTE < -0.2) ? ' There are significant staffing shortages that should be addressed through recruitment or workload redistribution.' : ' The overall staffing levels are relatively well-balanced, with minor adjustments needed in some departments.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>}
          </Card>

          {/* Teacher Workloads */}
          <Card>
            <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleSection('teachers')}>
              <h3 className="text-card-title text-ink-900">
                Teacher Workloads
              </h3>
              {expandedSection === 'teachers' ? <ChevronUp className="h-5 w-5 text-ink-500" /> : <ChevronDown className="h-5 w-5 text-ink-500" />}
            </div>
            {expandedSection === 'teachers' && <div className="mt-6 space-y-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
                  <div className="flex-1 flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
                    <div className="w-full md:w-64">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Search className="h-5 w-5 text-ink-400" />
                        </div>
                        <input type="text" className="block w-full pl-10 pr-3 py-2 border border-ink-300 rounded-md leading-5 bg-white placeholder-ink-500 focus:outline-none focus:placeholder-ink-400 focus:ring-1 focus:ring-navy-500 focus:border-navy-500 sm:text-sm" placeholder="Search teachers" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <select className="block pl-3 pr-10 py-2 text-base border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm rounded-md" value={filterDepartment} onChange={e => setFilterDepartment(e.target.value)}>
                        <option value="all">All Learning Areas</option>
                        {learningAreaNames.map(name => <option key={name} value={name}>
                            {name}
                          </option>)}
                      </select>
                      <select className="block pl-3 pr-10 py-2 text-base border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm rounded-md" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                        <option value="all">All Statuses</option>
                        <option value="underloaded">Underloaded</option>
                        <option value="optimal">Optimal</option>
                        <option value="overloaded">Overloaded</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-ink-200">
                    <thead className="bg-ink-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-meta font-medium text-ink-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('name')}>
                          <div className="flex items-center">
                            <span>Teacher</span>
                            {sortField === 'name' && <ArrowUpDown className="h-3 w-3 ml-1" />}
                          </div>
                        </th>
                        <th className="px-4 py-2 text-left text-meta font-medium text-ink-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('department')}>
                          <div className="flex items-center">
                            <span>Learning Area</span>
                            {sortField === 'department' && <ArrowUpDown className="h-3 w-3 ml-1" />}
                          </div>
                        </th>
                        <th className="px-3 py-2 text-left text-meta font-medium text-ink-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('fte')}>
                          <div className="flex items-center">
                            <span>FTE</span>
                            {sortField === 'fte' && <ArrowUpDown className="h-3 w-3 ml-1" />}
                          </div>
                        </th>
                        <th className="px-3 py-2 text-left text-meta font-medium text-ink-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('classes')}>
                          <div className="flex items-center">
                            <span>Classes</span>
                            {sortField === 'classes' && <ArrowUpDown className="h-3 w-3 ml-1" />}
                          </div>
                        </th>
                        <th className="px-3 py-2 text-left text-meta font-medium text-ink-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('semester1Utilization')}>
                          <div className="flex items-center">
                            <span>S1 Util</span>
                            {sortField === 'semester1Utilization' && <ArrowUpDown className="h-3 w-3 ml-1" />}
                          </div>
                        </th>
                        <th className="px-3 py-2 text-left text-meta font-medium text-ink-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('semester2Utilization')}>
                          <div className="flex items-center">
                            <span>S2 Util</span>
                            {sortField === 'semester2Utilization' && <ArrowUpDown className="h-3 w-3 ml-1" />}
                          </div>
                        </th>
                        <th className="px-4 py-2 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-2 text-right text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-ink-200">
                      {getFilteredTeacherWorkloads().length === 0 ? <tr>
                          <td colSpan={8} className="px-6 py-4 whitespace-nowrap text-center text-body-sm text-ink-500">
                            No teachers found matching the filters
                          </td>
                        </tr> : getFilteredTeacherWorkloads().map(teacher => <React.Fragment key={teacher.id}>
                            <tr className="hover:bg-ink-50">
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="text-body-sm font-medium text-ink-900">
                                  {teacher.name}
                                </div>
                                <div className="text-meta text-ink-500 font-mono tabular-nums">
                                  {teacher.code}
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-body-sm text-ink-500">
                                {displayLearningArea(teacher.department)}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                                {teacher.fte.toFixed(1)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                                {teacher.classes}
                                {teacher.polAllowance > 0 && <span className="ml-1 text-meta text-navy-600">
                                    +POL
                                  </span>}
                              </td>
                              <td className="px-3 py-3">
                                <div className="flex items-center space-x-1.5">
                                  <div className={`text-body-sm font-medium font-mono tabular-nums ${getUtilizationColor(teacher.semester1.utilization)} min-w-[36px]`}>
                                    {teacher.semester1.utilization}%
                                  </div>
                                  <div className="w-12 bg-ink-200 rounded-full h-1.5">
                                    <div className={`h-1.5 rounded-full ${teacher.semester1.utilization < 85 ? 'bg-navy-500' : teacher.semester1.utilization < 95 ? 'bg-ok-solid' : teacher.semester1.utilization <= 105 ? 'bg-ok-solid' : teacher.semester1.utilization <= 115 ? 'bg-warn-solid' : 'bg-danger-solid'}`} style={{
                            width: `${Math.min(teacher.semester1.utilization, 150)}%`
                          }}></div>
                                  </div>
                                </div>
                                <div className="text-meta text-ink-500 mt-0.5 font-mono tabular-nums">
                                  {teacher.semester1.allocatedMinutes}/{teacher.semester1.targetMinutes}
                                </div>
                              </td>
                              <td className="px-3 py-3">
                                <div className="flex items-center space-x-1.5">
                                  <div className={`text-body-sm font-medium font-mono tabular-nums ${getUtilizationColor(teacher.semester2.utilization)} min-w-[36px]`}>
                                    {teacher.semester2.utilization}%
                                  </div>
                                  <div className="w-12 bg-ink-200 rounded-full h-1.5">
                                    <div className={`h-1.5 rounded-full ${teacher.semester2.utilization < 85 ? 'bg-navy-500' : teacher.semester2.utilization < 95 ? 'bg-ok-solid' : teacher.semester2.utilization <= 105 ? 'bg-ok-solid' : teacher.semester2.utilization <= 115 ? 'bg-warn-solid' : 'bg-danger-solid'}`} style={{
                            width: `${Math.min(teacher.semester2.utilization, 150)}%`
                          }}></div>
                                  </div>
                                </div>
                                <div className="text-meta text-ink-500 mt-0.5 font-mono tabular-nums">
                                  {teacher.semester2.allocatedMinutes}/{teacher.semester2.targetMinutes}
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {getStatusBadge(teacher.status)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right text-body-sm font-medium">
                                <Button variant="secondary" size="sm" onClick={() => setSelectedTeacher(teacher.id === selectedTeacher ? null : teacher.id)}>
                                  {teacher.id === selectedTeacher ? 'Hide' : 'Details'}
                                </Button>
                              </td>
                            </tr>
                            {selectedTeacher === teacher.id && <tr>
                                <td colSpan={8} className="px-0 py-0">
                                  {renderTeacherDetails(teacher.id)}
                                </td>
                              </tr>}
                          </React.Fragment>)}
                    </tbody>
                  </table>
                </div>
              </div>}
          </Card>

          {/* Recommendations */}
          <Card>
            <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleSection('recommendations')}>
              <h3 className="text-card-title text-ink-900">
                Recommendations
              </h3>
              {expandedSection === 'recommendations' ? <ChevronUp className="h-5 w-5 text-ink-500" /> : <ChevronDown className="h-5 w-5 text-ink-500" />}
            </div>
            {expandedSection === 'recommendations' && <div className="mt-6 space-y-6">
                <div className="bg-navy-50 p-4 rounded-md border border-navy-200">
                  <div className="flex">
                    <Info className="h-5 w-5 text-navy-500 mr-3 mt-0.5" />
                    <div>
                      <h4 className="text-body-sm font-medium text-navy-800 mb-1">
                        Overall Recommendations
                      </h4>
                      <p className="text-body-sm text-navy-700">
                        Based on the workload equity analysis, here are some
                        recommendations to improve workload distribution:
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  {equityData.overallEquity < 80 && <div className="bg-white p-4 rounded-md border">
                      <h4 className="text-body-sm font-medium text-ink-900 mb-2">
                        Improve Overall Equity
                      </h4>
                      <p className="text-body-sm text-ink-700">
                        The overall equity score of {equityData.overallEquity}%
                        indicates room for improvement. Focus on redistributing
                        workload from overloaded teachers to underloaded
                        teachers to achieve better balance.
                      </p>
                    </div>}
                  {equityData.utilizationDistribution.find(d => d.range === '<70%')?.count > 0 && <div className="bg-white p-4 rounded-md border">
                      <h4 className="text-body-sm font-medium text-ink-900 mb-2">
                        Address Severe Underloading
                      </h4>
                      <p className="text-body-sm text-ink-700">
                        {equityData.utilizationDistribution.find(d => d.range === '<70%')?.count || 0}{' '}
                        teachers are severely underloaded (below 70%
                        utilization). Consider assigning additional classes or
                        responsibilities to these teachers to improve resource
                        utilization.
                      </p>
                    </div>}
                  {equityData.utilizationDistribution.find(d => d.range === '>115%')?.count > 0 && <div className="bg-white p-4 rounded-md border">
                      <h4 className="text-body-sm font-medium text-ink-900 mb-2">
                        Address Severe Overloading
                      </h4>
                      <p className="text-body-sm text-ink-700">
                        {equityData.utilizationDistribution.find(d => d.range === '>115%')?.count || 0}{' '}
                        teachers are severely overloaded (above 115%
                        utilization). Prioritize reducing the workload for these
                        teachers to prevent burnout and ensure teaching quality.
                      </p>
                    </div>}
                  {equityData.departmentEquity.some(d => d.equityScore < 70) && <div className="bg-white p-4 rounded-md border">
                      <h4 className="text-body-sm font-medium text-ink-900 mb-2">
                        Improve Learning Area Equity
                      </h4>
                      <p className="text-body-sm text-ink-700">
                        The following learning areas have low equity scores and
                        high workload variation:
                      </p>
                      <ul className="mt-2 space-y-1 text-body-sm text-ink-700">
                        {equityData.departmentEquity.filter(d => d.equityScore < 70).map((dept, index) => <li key={index}>
                              <span className="font-medium">
                                {displayLearningArea(dept.department)}
                              </span>
                              : {dept.equityScore}% equity score, with
                              utilization ranging from {dept.minUtilization}% to{' '}
                              {dept.maxUtilization}%
                            </li>)}
                      </ul>
                    </div>}
                  {equityData.workloadGaps.some(g => g.netFTE < -0.2) && <div className="bg-white p-4 rounded-md border">
                      <h4 className="text-body-sm font-medium text-ink-900 mb-2">
                        Address Staffing Shortages
                      </h4>
                      <p className="text-body-sm text-ink-700">
                        The following learning areas have significant staffing
                        shortages:
                      </p>
                      <ul className="mt-2 space-y-1 text-body-sm text-ink-700">
                        {equityData.workloadGaps.filter(g => g.netFTE < -0.2).map((gap, index) => <li key={index}>
                              <span className="font-medium">
                                {displayLearningArea(gap.department)}
                              </span>
                              : {gap.shortage.toFixed(2)} FTE shortage
                            </li>)}
                      </ul>
                      <p className="mt-2 text-body-sm text-ink-700">
                        Consider recruiting additional staff or redistributing
                        teaching load from these learning areas.
                      </p>
                    </div>}
                  {equityData.workloadGaps.some(g => g.netFTE > 0.2) && <div className="bg-white p-4 rounded-md border">
                      <h4 className="text-body-sm font-medium text-ink-900 mb-2">
                        Optimize Excess Capacity
                      </h4>
                      <p className="text-body-sm text-ink-700">
                        The following learning areas have excess staffing capacity:
                      </p>
                      <ul className="mt-2 space-y-1 text-body-sm text-ink-700">
                        {equityData.workloadGaps.filter(g => g.netFTE > 0.2).map((gap, index) => <li key={index}>
                              <span className="font-medium">
                                {displayLearningArea(gap.department)}
                              </span>
                              : {gap.overallocation.toFixed(2)} FTE surplus
                            </li>)}
                      </ul>
                      <p className="mt-2 text-body-sm text-ink-700">
                        Consider redistributing teaching load to these
                        learning areas or adjusting FTE allocations.
                      </p>
                    </div>}
                </div>
              </div>}
          </Card>
        </div>
      ) : (
        <Card className="p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-warn-fg mx-auto mb-4" />
          <h3 className="text-card-title text-ink-900 mb-2">
            No Equity Data Available
          </h3>
          <p className="text-ink-500 mb-4">
            Workload equity data has not been calculated yet. Click the "Recalculate" button
            to analyze workload distribution.
          </p>
          <Button
            variant="primary"
            leftIcon={<RefreshCw className="h-4 w-4" />}
            onClick={() => calculateEquity(teacherWorkloads)}
          >
            Calculate Equity
          </Button>
        </Card>
      )}
    </div>
  );
};

export default WorkloadEquity;
